// Error code → human-friendly English message.
// i18n hook: replace this with a real translator when locale support lands.

const messages: Record<string, string> = {
  AUTH_INVALID_CREDENTIALS: "Email or password is incorrect.",
  AUTH_LOCKED: "Too many failed attempts. Try again in a few minutes.",
  AUTH_PASSWORD_WEAK:
    "Password must be at least 12 characters with letters and numbers.",
  AUTH_TOKEN_INVALID: "This link is invalid or has expired.",
  AUTH_REFRESH_INVALID: "Your session has expired. Please sign in again.",
  AUTH_UNAUTHENTICATED: "Please sign in to continue.",
  AUTH_FORBIDDEN: "You do not have permission to perform this action.",
  AUTH_EMAIL_TAKEN: "We have sent a verification email if that address is available.",
  AUTH_EMAIL_MISMATCH:
    "This invitation was sent to a different email address.",
  TENANT_SLUG_TAKEN: "That workspace URL is already in use.",
  TENANT_SLUG_INVALID:
    "Workspace URL must be 3 to 32 characters, lowercase letters, numbers and hyphens.",
  TENANT_NOT_FOUND: "Workspace not found.",
  TENANT_NOT_MEMBER: "You are not a member of that workspace.",
  MEMBERSHIP_LAST_OWNER:
    "You cannot remove the last Owner of a workspace. Promote another member to Owner first.",
  MEMBERSHIP_ALREADY_MEMBER: "That person is already a member of this workspace.",
  MEMBERSHIP_OWNER_CANNOT_LEAVE:
    "Owners cannot leave a workspace. Transfer ownership or remove the workspace instead.",
  SUPER_PLAN_NOT_FOUND: "Selected plan no longer exists.",
  SUPER_IMPERSONATION_INVALID: "Impersonation request is invalid.",
  COMMON_RATE_LIMITED: "Too many requests. Please wait a moment and try again.",
  COMMON_VALIDATION: "Please check the highlighted fields.",
  COMMON_NETWORK: "Network error. Check your connection and try again.",
  COMMON_UNKNOWN: "Something went wrong. Please try again.",
  // ----- WP-B: sites, chatbots, widget, knowledge, prompts, leads, LLM keys.
  SITE_DOMAIN_TAKEN: "That domain is already registered.",
  SITE_DOMAIN_INVALID: "Use a host like example.co.nz or *.example.co.nz.",
  SITE_NOT_FOUND: "Site not found.",
  SITE_HAS_CHATBOTS:
    "This site still has chatbots attached. Reassign or delete them first.",
  CHATBOT_NOT_FOUND: "Chatbot not found.",
  CHATBOT_SLUG_TAKEN: "That chatbot slug is already taken in this workspace.",
  KNOWLEDGE_LIMIT:
    "Knowledge document limit reached for this chatbot. Archive an older one to add more.",
  WIDGET_ORIGIN_REJECTED:
    "The widget tried to load from a domain that is not on your allow list.",
  CHAT_TURN_LIMIT:
    "This visitor reached the per-session message limit. Try again later.",
  CHAT_MESSAGE_TOO_LONG: "That message is longer than the allowed 2000 characters.",
  CHAT_TOKEN_INVALID: "The widget session expired. Reload the page to try again.",
  CHAT_TIMEOUT: "The assistant took too long to respond. Try again.",
  CONVERSATION_NOT_FOUND: "Conversation not found.",
  LEAD_NO_CONTACT: "Add at least one contact field (email or phone).",
  LEAD_VALIDATION_EMAIL: "That email address looks invalid.",
  LEAD_VALIDATION_PHONE: "That phone number looks invalid.",
  LLM_KEY_INVALID: "That API key was rejected by the provider.",
  // ----- WP-D: conversation state machine + assignee + CSV export + notify.
  CONVERSATION_STATUS_INVALID_TRANSITION:
    "That status change is not allowed from the current state.",
  CONVERSATION_ASSIGNEE_NOT_MEMBER:
    "That teammate is not a member of this workspace anymore.",
  LEADS_EXPORT_RATE_LIMITED:
    "That export is too large. Narrow the date range and try again.",
  LEADS_EXPORT_TOO_LARGE:
    "That export exceeds the 100,000 row limit. Filter by date range and try again.",
  NOTIFICATION_EMAIL_INVALID: "That notification email address looks invalid.",
  NOTIFICATION_DIGEST_INVALID: "Daily digest send hour must be between 0 and 23.",
  CONVERSATION_SUMMARY_FAILED:
    "We couldn't generate that summary. Try again in a moment.",
  // ----- WP-E: web voice (chatbot voice settings + WS error codes).
  VOICE_PLAN_DISABLED:
    "Web voice is not available on your current plan. Upgrade to Starter or higher to enable it.",
  VOICE_NOT_ENABLED: "Voice is switched off for this chatbot.",
  VOICE_LIMIT_EXCEEDED:
    "You have used all the voice minutes included in this month's plan.",
  VOICE_CONCURRENCY_EXCEEDED:
    "Too many concurrent voice calls. Please try again shortly.",
  VOICE_SESSION_TIMEOUT:
    "The voice session reached the maximum duration and was ended.",
  VOICE_PROVIDER_FAILURE:
    "The voice provider is temporarily unavailable. Try again in a few minutes.",
  VOICE_TOKEN_INVALID: "The voice session token is no longer valid.",
  // ----- WP-F: Twilio integration + phone numbers.
  TWILIO_CONFIG_MISSING:
    "Configure Twilio before adding phone numbers.",
  TWILIO_AUTH_INVALID:
    "Twilio Account SID or Auth Token looks invalid. Double-check the values from your Twilio console.",
  PHONE_NUMBER_IN_USE:
    "That phone number is already attached to this workspace.",
  PHONE_NUMBER_INVALID:
    "Phone number must be in E.164 format, e.g. +64-9-555-0000.",
  PHONE_LIMIT_EXCEEDED:
    "You have reached the number-of-phone-numbers limit for your plan.",
  PHONE_PLAN_NOT_SUPPORTED:
    "Phone is not available on your current plan. Upgrade to Professional or higher to enable it.",
  PHONE_NUMBER_RELEASED:
    "Phone number released.",
};

export function translateError(code: string | undefined, fallback?: string): string {
  if (!code) return fallback ?? messages.COMMON_UNKNOWN;
  return messages[code] ?? fallback ?? messages.COMMON_UNKNOWN;
}
